import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import apiClient from '../api/client';
import MarketplaceFilter from '../components/MarketplaceFilter';

const fmtMoney = (v) =>
  v != null ? Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) : '0';
const fmtPct = (v) => (v != null ? `${Number(v).toFixed(1)}%` : '—');

function toDateKey(v) {
  if (v == null) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v).slice(0, 10);
}

export default function MvpDashboard({ clientId }) {
  const [marketplace, setMarketplace] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get('/mvp/dashboard', { params: { client_id: clientId, marketplace } })
      .then(({ data: d }) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.message || 'Ошибка загрузки');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [clientId, marketplace]);

  const chartData = useMemo(() => {
    if (!data?.daily?.length) return [];
    const byDate = new Map();
    for (const row of data.daily) {
      const key = toDateKey(row.date);
      if (!byDate.has(key)) {
        byDate.set(key, { date: key, revenue: 0, spend: 0, orders: 0 });
      }
      const agg = byDate.get(key);
      agg.revenue += Number(row.revenue) || 0;
      agg.spend += Number(row.spend) || 0;
      agg.orders += Number(row.orders) || 0;
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  if (loading) return <p className="text-[var(--wb-text-muted)]">Загрузка дашборда…</p>;
  if (error) return <p className="text-[var(--wb-danger)]">{error}</p>;

  const s = data?.summary || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[var(--wb-text-primary)]">Сводный дашборд</h2>
          <p className="text-sm text-[var(--wb-text-muted)]">
            WB · Ozon · Авито · Avito Ads — демо-данные за 30 дней
          </p>
        </div>
        <MarketplaceFilter
          value={marketplace}
          onChange={setMarketplace}
          available={['wb', 'ozon', 'avito', 'avito_ads']}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Выручка / цели', value: fmtMoney(s.revenue), sub: '₽' },
          { label: 'Рекламный расход', value: fmtMoney(s.spend), sub: '₽' },
          { label: 'Заказы / лиды', value: fmtMoney(s.orders), sub: '' },
          { label: 'ДРР', value: fmtPct(s.drr_pct), sub: `ROI ${fmtPct(s.roi_pct)}` },
        ].map((kpi) => (
          <div key={kpi.label} className="wb-card p-4">
            <p className="text-xs text-[var(--wb-text-muted)]">{kpi.label}</p>
            <p className="text-2xl font-bold mt-1">{kpi.value}</p>
            {kpi.sub && <p className="text-xs text-[var(--wb-text-muted)]">{kpi.sub}</p>}
          </div>
        ))}
      </div>

      <div className="wb-card p-4">
        <h3 className="text-sm font-semibold mb-4">Динамика по дням</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--wb-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Line type="monotone" dataKey="revenue" name="Выручка/цели" stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="spend" name="Расход" stroke="#ef4444" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="orders" name="Заказы/лиды" stroke="#6366f1" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
